import { useCallback } from 'react';
import { useAppNavigate } from './useAppNavigate';
import { useAppSelector } from '../redux/hooks';
import type { NavItem } from '../components/navigation';

/**
 * Centralized navigation hook - Single source of truth for all navigation logic
 * Used by: ProtectedLayout, HomePage, DynamicPage, all nav widgets, banner widgets, layout widgets
 */
export const useNavigation = () => {
  const appNavigate = useAppNavigate();
  const { isEditMode } = useAppSelector((state) => state.editMode);
  
  /**
   * Handle navigation item click (for nav menus)
   */
  const handleNavClick = useCallback((item: NavItem) => {
    // Don't navigate if in edit mode
    // if (isEditMode) return;
    
    // Use item.url directly if it exists, otherwise construct from label
    const url = item.url || `/${item.label.toLowerCase().replace(/\s+/g, '-')}`;
    
    // Handle external URLs
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank');
      return;
    }
    
    // Handle internal navigation
    appNavigate(url);
  }, [appNavigate, isEditMode]);
  
  /**
   * Handle logo click (navigate to home)
   */
  const handleLogoClick = useCallback(() => {
    // Navigate to home page (works in both view and edit mode)
    appNavigate('/');
  }, [appNavigate]);
  
  /**
   * Handle button/link click (for banners, CTAs, etc.)
   * Navigation works in all modes (edit, preview, and normal)
   */
  const handleButtonClick = useCallback((url: string) => {
    // Handle external URLs
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank');
      return;
    }
    
    // Handle internal navigation
    appNavigate(url);
  }, [appNavigate, isEditMode]);
  
  return {
    handleNavClick,
    handleLogoClick,
    handleButtonClick,
  };
};
